import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/seo";
import { GAMME_TEXT, photoFor } from "./Products";
import { CAT_LABEL } from "../../content/universes";

const CUT = { homme: "Coupe homme", femme: "Coupe femme", unisexe: "Unisexe", enfant: "Enfant" };
const QUOTE_CAT = { tshirt: ["vetements", "tshirt"], polo: ["vetements", "polo"], sweat: ["vetements", "sweat"], veste: ["vetements", "veste"], chemise: ["vetements", "veste"], casquette: ["accessoires", "casquette"] };

export default function ProductPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useT();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setD(null); api.get(`/public/products/${slug}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [slug]);
  const p = d?.product;
  useSeo({ title: p ? `${p.name} — ${p.brand} ${p.ref} personnalisé | KOUOH` : "Fiche produit", description: p ? `${p.name} ${p.brand} ${p.ref}, ${p.grammage || ""} g/m². Personnalisation broderie ou impression à Nice. Devis gratuit.` : "", path: `/produits/${slug}` });
  if (err) return <section className="k-section pt-12"><p>{t("Fiche introuvable.")}</p><Link to="/produits" className="k-link">{t("Voir les produits")}</Link></section>;
  if (!p) return <section className="k-section pt-12 text-sm text-[#8C857B]">…</section>;
  const g = p.gamme ? GAMME_TEXT[p.gamme] : null;
  const cuts = d.family.length > 1 ? d.family : [];
  const [qOffer, qCat] = QUOTE_CAT[p.category] || ["accessoires", "accessoires"];
  const quote = () => nav(`/demande/${qOffer}?ref=${encodeURIComponent(p.ref)}&cat=${qCat}&name=${encodeURIComponent(p.name)}`);
  const infos = [
    [t("Matière"), `${p.composition}${p.grammage ? ` · ${p.grammage} g/m²` : ""}`],
    [t("Coupe"), `${t(CUT[p.cut] || p.cut)} · ${t("manches")} ${t(p.sleeves)}`],
    [t("Pour qui"), g ? `${g[0]} — ${t(g[2])}` : p.advice || t("Équipes, événements, boutiques")],
  ];

  return (
    <section className="k-section pt-12" data-testid="product-page">
      <nav className="text-xs text-[#8C857B]"><Link to="/produits" className="k-link">{t("Produits")}</Link> › {t(CAT_LABEL[p.category] || p.category)}</nav>
      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px] items-start">
        <div className="aspect-[4/5] max-h-[640px] bg-[#F4F0EA] overflow-hidden"><img src={photoFor(p)} alt={`${p.name} — ${p.brand} ${p.ref}`} className="w-full h-full object-cover object-[center_20%]" data-testid="product-photo" /></div>
        <div className="lg:sticky lg:top-28">
          {g && <p className="k-eyebrow" data-testid="product-gamme">{g[0]}</p>}
          <h1 className="k-h1 mt-2" data-testid="product-name">{p.name}</h1>
          <p className="text-sm text-[#8C857B] mt-2">{p.brand} · {t("réf.")} {p.ref}</p>
          {cuts.length > 0 && (
            <div className="mt-4 flex gap-2" data-testid="product-cuts">{cuts.map((c) => <Link key={c.ref} to={`/produits/${c.slug}`} aria-current={c.ref === p.ref} className={`px-3 py-1.5 text-sm border ${c.ref === p.ref ? "bg-[#1C1917] text-[#FBF9F5] border-[#1C1917]" : "border-[#D6CEBF]"}`} data-testid={`cut-${c.ref}`}>{t(CUT[c.cut] || c.cut)}</Link>)}</div>
          )}
          <dl className="mt-8 grid gap-4 text-[15px]" data-testid="product-specs">
            {infos.map(([k, v]) => <div key={k} className="border-t border-[#E8E2D8] pt-3"><dt className="k-eyebrow !text-[0.6rem]">{k}</dt><dd className="mt-1">{v}</dd></div>)}
          </dl>
          <button type="button" onClick={quote} className="k-btn mt-8 w-full sm:w-auto justify-center" data-testid="product-quote-btn">{t("Demander mon devis")} <ArrowRight size={16} /></button>
          <p className="text-xs text-[#8C857B] mt-3">{t("Gratuit · Sans engagement · Coloris, tailles et logo se règlent ensemble au devis.")}</p>
        </div>
      </div>
    </section>
  );
}
